
import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { toast } from "sonner";

// JWT Token Types
interface JWTTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface User {
  id: string;
  name: string;
  email: string;
  photoUrl?: string;
  role?: "patient" | "psychologist" | "admin";
}

interface Appointment {
  id: string;
  date: string;
  time: string;
  duration: number;
  status: "scheduled" | "completed" | "canceled" | "pending_payment";
  meetingUrl?: string;
  psychologist: {
    name: string;
    photoUrl?: string;
  };
}

interface UserContextType {
  user: User | null;
  tokens: JWTTokens | null;
  isLoading: boolean;
  appointments: Appointment[];
  login: (email: string, password: string) => Promise<boolean>;
  register: (name: string, email: string, password: string, role: "patient" | "psychologist") => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  bookAppointment: (date: string, time: string, duration: number) => Promise<boolean>;
  refreshTokens: () => Promise<boolean>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

// Local Storage Keys
const TOKEN_STORAGE_KEY = "psi_online_tokens";
const USER_STORAGE_KEY = "psi_online_user";

// Token helpers
const saveTokensToStorage = (tokens: JWTTokens) => {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  } catch (error) {
    console.error("Error saving tokens to localStorage:", error);
  }
};

const getTokensFromStorage = (): JWTTokens | null => {
  try {
    const tokensStr = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!tokensStr) return null;
    return JSON.parse(tokensStr);
  } catch (error) {
    console.error("Error getting tokens from localStorage:", error);
    return null;
  }
};

const saveUserToStorage = (user: User) => {
  try {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  } catch (error) {
    console.error("Error saving user to localStorage:", error);
  }
};

const getUserFromStorage = (): User | null => {
  try {
    const userStr = localStorage.getItem(USER_STORAGE_KEY);
    if (!userStr) return null;
    return JSON.parse(userStr);
  } catch (error) {
    console.error("Error getting user from localStorage:", error);
    return null;
  }
};

const clearStorage = () => {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing localStorage:", error);
  }
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(getUserFromStorage());
  const [tokens, setTokens] = useState<JWTTokens | null>(getTokensFromStorage());
  const [isLoading, setIsLoading] = useState(false);
  const [appointments, setAppointments] = useState<Appointment[]>([
    {
      id: "1",
      date: "2025-05-25",
      time: "14:30",
      duration: 60,
      status: "scheduled",
      meetingUrl: "https://meet.google.com/abc-defg-hij",
      psychologist: {
        name: "Dra. Ana Silva",
        photoUrl: "https://randomuser.me/api/portraits/women/44.jpg"
      }
    },
    {
      id: "2",
      date: "2025-05-18",
      time: "10:00",
      duration: 45,
      status: "completed",
      meetingUrl: "https://meet.google.com/xyz-uvwx-rst",
      psychologist: {
        name: "Dr. Carlos Mendes",
        photoUrl: "https://randomuser.me/api/portraits/men/32.jpg"
      }
    }
  ]);

  // Check token expiration and refresh if needed
  useEffect(() => {
    if (!tokens) return;

    const currentTime = Date.now();
    const timeToExpire = tokens.expiresAt - currentTime;
    
    // If token expires in less than 5 minutes, refresh it
    if (timeToExpire < 5 * 60 * 1000) {
      refreshTokens();
    }
    
    // Set up a timer to refresh tokens before expiry
    const refreshTimer = setTimeout(() => {
      refreshTokens();
    }, timeToExpire - 5 * 60 * 1000); // 5 minutes before expiry
    
    return () => clearTimeout(refreshTimer);
  }, [tokens]);

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // For demo purposes, specific credentials offer different roles
      let userData: User | null = null;
      
      if (email === "admin@example.com" && password === "password") {
        userData = {
          id: "admin1",
          name: "Admin",
          email: "admin@example.com",
          role: "admin"
        };
      } else if (email === "psi@example.com" && password === "password") {
        userData = {
          id: "psi1",
          name: "Dr. João Silva",
          email: "psi@example.com",
          role: "psychologist"
        };
      } else if (email === "test@example.com" && password === "password") {
        userData = {
          id: "user1",
          name: "Usuário Teste",
          email: "test@example.com",
          role: "patient"
        };
      }
      
      if (userData) {
        // Generate mock tokens
        const newTokens: JWTTokens = {
          accessToken: `mock_access_token_${Date.now()}`,
          refreshToken: `mock_refresh_token_${Date.now()}`,
          expiresAt: Date.now() + 1 * 60 * 60 * 1000 // 1 hour
        };
        
        setUser(userData);
        setTokens(newTokens);
        
        // Save to storage
        saveUserToStorage(userData);
        saveTokensToStorage(newTokens);
        
        toast.success("Login realizado com sucesso!");
        return true;
      } else {
        toast.error("Credenciais inválidas. Tente novamente.");
        return false;
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Erro ao fazer login. Tente novamente.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    name: string, 
    email: string, 
    password: string,
    role: "patient" | "psychologist"
  ): Promise<boolean> => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Create user
      const userData: User = {
        id: `user-${Date.now()}`,
        name,
        email,
        role
      };
      
      // Generate mock tokens
      const newTokens: JWTTokens = {
        accessToken: `mock_access_token_${Date.now()}`,
        refreshToken: `mock_refresh_token_${Date.now()}`,
        expiresAt: Date.now() + 1 * 60 * 60 * 1000 // 1 hour
      };
      
      setUser(userData);
      setTokens(newTokens);
      
      // Save to storage
      saveUserToStorage(userData);
      saveTokensToStorage(newTokens);
      
      toast.success("Cadastro realizado com sucesso!");
      return true;
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Erro ao registrar. Tente novamente.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setTokens(null);
    clearStorage();
    toast.success("Logout realizado com sucesso!");
  };

  const refreshTokens = async (): Promise<boolean> => {
    if (!tokens?.refreshToken) return false;
    
    try {
      // Simulate API call to refresh tokens
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Generate new tokens
      const newTokens: JWTTokens = {
        accessToken: `mock_access_token_${Date.now()}`,
        refreshToken: tokens.refreshToken, // Usually refresh token stays the same
        expiresAt: Date.now() + 1 * 60 * 60 * 1000 // 1 hour
      };
      
      setTokens(newTokens);
      saveTokensToStorage(newTokens);
      
      return true;
    } catch (error) {
      console.error("Token refresh error:", error);
      // On refresh token failure, log the user out
      logout();
      return false;
    }
  };

  const bookAppointment = async (date: string, time: string, duration: number): Promise<boolean> => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newAppointment: Appointment = {
        id: `app-${Date.now()}`,
        date,
        time,
        duration,
        status: "pending_payment",
        psychologist: {
          name: "Dra. Ana Silva",
          photoUrl: "https://randomuser.me/api/portraits/women/44.jpg"
        }
      };
      
      setAppointments(prev => [newAppointment, ...prev]);
      return true;
    } catch (error) {
      console.error("Appointment booking error:", error);
      toast.error("Erro ao agendar consulta. Tente novamente.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        tokens,
        isLoading,
        appointments,
        login,
        register,
        logout,
        isAuthenticated: !!user && !!tokens,
        bookAppointment,
        refreshTokens
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
